import { useMemo } from "react";
import type { IGroupedVirtualizedSelectProps } from "../index";
import { useGroupedVirtualizedAllSelection } from "./all";
import { useGroupedVirtualizedGroupSelection } from "./group";
import { useGroupedVirtualizedSelection } from "./item";

export enum CheckStatus {
  NONE = 0,
  UNCHECKED = 1,
  CHECKED = 2,
  INDETERMINATE = 3,
}

export const useMixSlotProps = <Data, Context>(
  slotProps?: IGroupedVirtualizedSelectProps<Data, Context>["slotProps"]
) => {
  return useMemo(() => {
    if (!slotProps) {
      return [];
    }
    const {
      selectHeader,
      selectHeaderCheckbox,
      selectGroup,
      selectGroupCheckbox,
      selectItem,
      selectItemCheckbox,
      ...groupedSlotProps
    } = slotProps;
    return [
      { selectHeader, selectHeaderCheckbox, selectGroup, selectGroupCheckbox, selectItem, selectItemCheckbox },
      groupedSlotProps,
    ] as const;
  }, [slotProps]);
};

export const useGroupedVirtualizedSelect = <Data>(
  data: readonly Data[],
  groupCounts: readonly number[],
  controlledSelectedItems: readonly Data[] | undefined,
  onSelectedItemsChange: ((items: readonly Data[]) => void) | undefined,
  getItemKey: (item: Data) => string
) => {
  const { selectedItems, isSelected, addItem, addItems, deleteItem, deleteItems } = useGroupedVirtualizedSelection(
    controlledSelectedItems,
    onSelectedItemsChange,
    getItemKey
  );
  const { getGroupCheckStatus, addGroup, deleteGroup } = useGroupedVirtualizedGroupSelection(
    data,
    groupCounts,
    isSelected,
    addItems,
    deleteItems
  );
  const { getAllCheckStatus, addAll, deleteAll } = useGroupedVirtualizedAllSelection(
    data,
    isSelected,
    addItems,
    deleteItems
  );

  return useMemo(
    () => ({
      selectedItems,
      isSelected,
      addItem,
      deleteItem,
      getGroupCheckStatus,
      addGroup,
      deleteGroup,
      getAllCheckStatus,
      addAll,
      deleteAll,
    }),
    [
      selectedItems,
      isSelected,
      addItem,
      deleteItem,
      getGroupCheckStatus,
      addGroup,
      deleteGroup,
      getAllCheckStatus,
      addAll,
      deleteAll,
    ]
  );
};
